import React, { createContext, useContext, useState, useEffect } from 'react';
import { cartAPI } from '../services/api';
import { useAuth } from './AuthContextNew';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);

  const { user, isGuest } = useAuth();
  
  // Function to get user ID from JWT token
  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('customerToken');
      if (!token) return null;
      
      // Decode JWT token (base64 decode the payload part)
      const payload = token.split('.')[1];
      if (!payload) return null;
      
      const decodedPayload = JSON.parse(atob(payload));
      
      // Try multiple possible field names for user ID
      return decodedPayload.id || 
             decodedPayload.userId || 
             decodedPayload._id || 
             decodedPayload.customerId ||
             decodedPayload.user?.id ||
             decodedPayload.user?._id;
    } catch (error) {
      console.error('❌ Error decoding JWT token:', error);
      return null;
    }
  };
  
  // Get user ID from token if user object doesn't have it
  const effectiveUserId = user?.id || user?._id || getUserIdFromToken();

  // Sync guest cart to backend when user becomes authenticated
  const syncGuestCartToBackend = async () => {
    try {
      const savedCart = localStorage.getItem('cart');
      if (!savedCart) {
        return;
      }

      // Get user from localStorage directly to avoid stale closure issues
      const storedUser = localStorage.getItem('user');
      let currentUser = user;
      if (!currentUser && storedUser) {
        try {
          currentUser = JSON.parse(storedUser);
        } catch (e) {
          console.error('Error parsing stored user:', e);
        }
      }

      // Recalculate user ID to ensure we have the latest value after auth state change
      // Try multiple sources: user object, token, or wait a bit and retry
      let currentUserId = currentUser?.id || currentUser?._id || currentUser?.customerId || getUserIdFromToken();
      
      // If still no user ID, wait a bit and retry (state might still be updating)
      if (!currentUserId) {
        console.log('⏳ [CART CONTEXT] User ID not available yet, waiting...');
        await new Promise(resolve => setTimeout(resolve, 200));
        // Re-read from localStorage after delay
        const retryStoredUser = localStorage.getItem('user');
        let retryUser = user;
        if (!retryUser && retryStoredUser) {
          try {
            retryUser = JSON.parse(retryStoredUser);
          } catch (e) {
            // Ignore parse errors
          }
        }
        currentUserId = retryUser?.id || retryUser?._id || retryUser?.customerId || getUserIdFromToken();
      }
      
      if (!currentUserId) {
        console.warn('⚠️ [CART CONTEXT] Cannot sync guest cart: User ID not available');
        return;
      }

      const cartData = JSON.parse(savedCart);
      const guestItems = cartData.items || [];
      
      if (guestItems.length === 0) {
        // No guest cart items to sync
        localStorage.removeItem('cart');
        return;
      }

      console.log('🔄 [CART CONTEXT] Syncing guest cart items to backend...', guestItems.length);
      
      // Sync each guest cart item to backend
      let syncSuccessCount = 0;
      for (const item of guestItems) {
        try {
          const isService = item.itemType === 'service';
          const productId = isService ? null : item.id;
          const serviceId = isService ? item.id : null;
          const providerId = isService ? item.providerId : 'store';
          const providerName = isService ? item.providerName : null;
          
          // Add item to backend cart with the quantity from guest cart
          const response = await cartAPI.addToCustomerCart(
            null, 
            productId, 
            item.quantity || 1, 
            serviceId, 
            item.itemType || 'product', 
            providerId, 
            providerName, 
            {}
          );
          
          if (response.success) {
            syncSuccessCount++;
          } else {
            console.warn('⚠️ [CART CONTEXT] Failed to sync item to backend:', item.name, response.message);
          }
        } catch (error) {
          console.error('❌ [CART CONTEXT] Error syncing item to backend:', error);
        }
      }
      
      // Clear guest cart from localStorage after sync attempt (even if some failed)
      // This prevents infinite retry loops
      localStorage.removeItem('cart');
      
      if (syncSuccessCount > 0) {
        console.log(`✅ [CART CONTEXT] Guest cart synced to backend successfully (${syncSuccessCount}/${guestItems.length} items)`);
      } else {
        console.warn('⚠️ [CART CONTEXT] Failed to sync any guest cart items to backend');
      }
      
      // Reload cart from backend to get updated data
      await refreshCart();
    } catch (error) {
      console.error('❌ [CART CONTEXT] Error syncing guest cart:', error);
      // Clear guest cart even on error to prevent retry loops
      localStorage.removeItem('cart');
    }
  };

  // Load cart function (extracted for reuse)
  const loadCartWithAuth = async () => {
    try {
      // Check authentication more reliably: token + user (not just isGuest)
      // Read from localStorage directly to avoid stale closure issues
      const token = localStorage.getItem('token') || localStorage.getItem('customerToken');
      const storedUser = localStorage.getItem('user');
      let currentUser = user;
      
      // If user from context is not available, try to get from localStorage
      if (!currentUser && storedUser) {
        try {
          currentUser = JSON.parse(storedUser);
        } catch (e) {
          console.error('Error parsing stored user:', e);
        }
      }
      
      const isAuthenticated = !!(token && currentUser);
      
      if (isAuthenticated) {
        // User is authenticated - check if there's a guest cart to sync
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          try {
            const cartData = JSON.parse(savedCart);
            const hasGuestItems = cartData.items && cartData.items.length > 0;
            
            if (hasGuestItems) {
              // User just became authenticated - sync guest cart first
              console.log('🔄 [CART CONTEXT] User authenticated, syncing guest cart...');
              await syncGuestCartToBackend();
            } else {
              // No guest items, just load from backend
              await refreshCart();
            }
          } catch (error) {
            console.error('Error parsing guest cart:', error);
            // If parsing fails, just load from backend
            await refreshCart();
          }
        } else {
          // No guest cart, just load from backend
          await refreshCart();
        }
      } else {
        // User is not authenticated - load from localStorage for guest users
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          try {
            const cartData = JSON.parse(savedCart);
            setItems(cartData.items || []);
            setTotalItems(cartData.totalItems || 0);
          } catch (error) {
            console.error('Error loading cart from localStorage:', error);
          }
        } else {
          // No guest cart either, clear cart state
          setItems([]);
          setTotalItems(0);
        }
      }
    } catch (error) {
      console.error('Cart Context - Auth check failed:', error);
    }
  };

  // Listen for user authentication events (from signup/login)
  useEffect(() => {
    const handleUserAuthenticated = async () => {
      console.log('🔄 [CART CONTEXT] User authenticated event received, syncing cart...');
      // Wait longer to ensure auth state is fully propagated through React
      // AuthContext updates localStorage immediately, but React state might take a moment
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force reload by calling loadCartWithAuth which reads from localStorage
      await loadCartWithAuth();
    };

    window.addEventListener('userAuthenticated', handleUserAuthenticated);
    return () => window.removeEventListener('userAuthenticated', handleUserAuthenticated);
  }, []); // Empty deps - function reads from localStorage directly, not from closure

  // Load cart from backend on mount and when auth state changes
  useEffect(() => {
    // Small delay to ensure state is consistent, but not too long
    const timeoutId = setTimeout(() => {
      loadCartWithAuth();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [user, isGuest]);



  const refreshCart = async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);
      
      // Debug authentication before making the call
      const token = localStorage.getItem('token') || localStorage.getItem('customerToken');
      
      if (!token && retryCount < 3) {
        setTimeout(() => {
          refreshCart(retryCount + 1);
        }, 1000);
        return;
      }
      
      // Get user ID from multiple sources to avoid stale closure issues
      // Try from context first, then localStorage, then token
      const storedUser = localStorage.getItem('user');
      let currentUser = user;
      if (!currentUser && storedUser) {
        try {
          currentUser = JSON.parse(storedUser);
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      const userId = currentUser?.id || currentUser?._id || currentUser?.customerId || getUserIdFromToken();
      
      // Check if we have a valid user ID
      if (!userId) {
        if (retryCount < 3) {
          console.log(`⏳ [CART CONTEXT] User ID not available, retrying... (${retryCount + 1}/3)`);
          setTimeout(() => {
            refreshCart(retryCount + 1);
          }, 500);
          return;
        }
        console.error('❌ Cart Context - No valid user ID found after retries');
        setError('User ID not found');
        return;
      }
      
      const response = await cartAPI.getCustomerCart(null);
      
      if (response.success) {
        
        // The cart data is in response.data.cart (not response.message.cart)
        const cartData = response.data?.cart;
        
        if (!cartData || !cartData.items) {
          console.error('❌ Cart Context - No cart data or items found');
          throw new Error('Invalid cart data structure');
        }
        
        // Transform backend cart data to match frontend format
        const transformedItems = cartData.items.map(item => {
          return {
            id: item.productId || item.serviceId, // This is the product/service ID from the backend
            _id: item.id, // This is the cart item ID
            cartItemId: item.id, // This is the MongoDB ObjectId of the cart item for API calls
            name: item.name,
            price: item.price,
            originalPrice: item.originalPrice,
            discount: item.discount,
            quantity: item.quantity,
            image: item.image,
            stock: item.stock,
            itemType: item.itemType || 'product',
            providerId: item.providerId || 'store',
            providerName: item.providerName || (item.providerId === 'store' ? 'NISTYLEZ Store' : item.providerId || 'Provider'),
            duration: item.duration || item.service?.duration // Add duration field for services
          };
        });
        
        setItems(transformedItems);
        setTotalItems(cartData.itemCount || 0);
      } else {
        throw new Error(response.message || 'Failed to load cart');
      }
    } catch (error) {
      console.error('Cart Context - Error loading cart:', error);
      
      // Retry if it's an authentication error and we haven't exceeded retry limit
      if (error.message?.includes('Access denied') && retryCount < 3) {
        setTimeout(() => {
          refreshCart(retryCount + 1);
        }, 2000);
        return;
      }
      
      setError('Failed to load cart');
      // Fallback to localStorage if API fails
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        try {
          const cartData = JSON.parse(savedCart);
          setItems(cartData.items || []);
          setTotalItems(cartData.totalItems || 0);
        } catch (e) {
          console.error('Error loading cart from localStorage:', e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (item) => {
    try {
      setLoading(true);
      setError(null);
      
      if (isGuest) {
        // Handle guest cart with localStorage
        const existingItem = items.find(i => i.id === item.id);
        if (existingItem) {
          const updatedItems = items.map(i => 
            i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
          );
          setItems(updatedItems);
          setTotalItems(prev => prev + 1);
          localStorage.setItem('cart', JSON.stringify({ items: updatedItems, totalItems: totalItems + 1 }));
        } else {
          const newItems = [...items, { ...item, quantity: 1 }];
          setItems(newItems);
          setTotalItems(prev => prev + 1);
          localStorage.setItem('cart', JSON.stringify({ items: newItems, totalItems: totalItems + 1 }));
        }
      } else {
        // Handle authenticated cart with API
        if (!effectiveUserId) {
          console.error('❌ Cart Context - No valid user ID for addToCart');
          setError('User ID not found');
          return;
        }
        
        
        // Determine if it's a product or service
        const isService = item.itemType === 'service';
        const productId = isService ? null : item.id;
        const serviceId = isService ? item.id : null;
        const providerId = isService ? item.providerId : 'store';
        const providerName = isService ? item.providerName : null;
        
        
        const response = await cartAPI.addToCustomerCart(null, productId, 1, serviceId, item.itemType, providerId, providerName, {});
        
        if (response.success) {
       //   console.log('✅ Cart Context - Item added successfully, reloading cart...');
          // Force reload cart from API to get updated data
          setTimeout(async () => {
            try {
        //      console.log('🔄 Cart Context - Reloading cart from API...');
              const cartResponse = await cartAPI.getCustomerCart(null);
         //     console.log('🔄 Cart Context - Cart reload response:', cartResponse);
              
              if (cartResponse.success) {
          //      console.log('🔄 Cart Context - Cart reload response:', cartResponse);
                
                // The cart data is in response.data.cart (not response.message.cart)
                const cartData = cartResponse.data?.cart;
           //     console.log('🔄 Cart Context - Cart data from reload:', cartData);
                
                if (cartData && cartData.items) {
                  const transformedItems = cartData.items.map(item => {
                    return {
                      id: item.productId || item.serviceId,
                      _id: item.id,
                      cartItemId: item.id, // This is the MongoDB ObjectId of the cart item
                      name: item.name,
                      price: item.price,
                      originalPrice: item.originalPrice,
                      discount: item.discount,
                      quantity: item.quantity,
                      image: item.image,
                      stock: item.stock,
                      itemType: item.itemType || 'product',
                      providerId: item.providerId || 'store',
                      providerName: item.providerName || (item.providerId === 'store' ? 'NISTYLEZ Store' : item.providerId || 'Provider'),
                      duration: item.duration || item.service?.duration // Add duration field for services
                    };
                  });
                  setItems(transformedItems);
                  setTotalItems(cartData.itemCount || 0);
                }
              } else {
                console.error('❌ Cart Context - Failed to reload cart:', cartResponse);
              }
            } catch (error) {
              console.error('❌ Cart Context - Error reloading cart:', error);
            }
          }, 100);
        } else {
          console.error('❌ Cart Context - Failed to add item:', response);
          throw new Error(response.message || 'Failed to add item to cart');
        }
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      setError('Failed to add item to cart: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (cartItemId) => {
    try {
      setLoading(true);
      setError(null);
      
      if (isGuest) {
        // Handle guest cart with localStorage
        const updatedItems = items.filter(item => item.cartItemId !== cartItemId);
        const removedItem = items.find(item => item.cartItemId === cartItemId);
        const newTotalItems = totalItems - (removedItem ? removedItem.quantity : 0);
        setItems(updatedItems);
        setTotalItems(newTotalItems);
        localStorage.setItem('cart', JSON.stringify({ items: updatedItems, totalItems: newTotalItems }));
      } else {
        // Handle authenticated cart with API
        const response = await cartAPI.removeFromCart(cartItemId);
        if (response.success) {
          // Update local state immediately
          const removedItem = items.find(item => item.cartItemId === cartItemId);
          const updatedItems = items.filter(item => item.cartItemId !== cartItemId);
          const newTotalItems = totalItems - (removedItem ? removedItem.quantity : 0);
          setItems(updatedItems);
          setTotalItems(newTotalItems);
        } else {
          throw new Error(response.message || 'Failed to remove item from cart');
        }
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      setError('Failed to remove item from cart: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (cartItemId, quantity) => {
    if (quantity <= 0) {
      await removeFromCart(cartItemId);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      if (isGuest) {
        // Handle guest cart with localStorage
        const updatedItems = items.map(item => 
          item.cartItemId === cartItemId ? { ...item, quantity } : item
        );
        const newTotalItems = updatedItems.reduce((total, item) => total + item.quantity, 0);
        setItems(updatedItems);
        setTotalItems(newTotalItems);
        localStorage.setItem('cart', JSON.stringify({ items: updatedItems, totalItems: newTotalItems }));
      } else {
        // Handle authenticated cart with API
        const response = await cartAPI.updateCartItemQuantity(cartItemId, quantity);
        if (response.success) {
          // Reload cart from API
          await refreshCart();
        } else {
          throw new Error(response.message || 'Failed to update cart item');
        }
      }
    } catch (error) {
      console.error('Error updating cart item:', error);
      setError('Failed to update cart item: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (isGuest) {
        // Handle guest cart with localStorage
        setItems([]);
        setTotalItems(0);
        localStorage.removeItem('cart');
      } else {
        // Handle authenticated cart with API
        const response = await cartAPI.clearCart();
        if (response.success) {
          setItems([]);
          setTotalItems(0);
        } else {
          throw new Error(response.message || 'Failed to clear cart');
        }
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      setError('Failed to clear cart: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getItemQuantity = (itemId) => {
    const item = items.find(i => i.id === itemId || i._id === itemId);
    return item ? item.quantity : 0;
  };

  const isInCart = (itemId) => {
    return items.some(item => item.id === itemId || item._id === itemId);
  };

  const getCartTotal = (providerId = null) => {
    const filteredItems = providerId 
      ? items.filter(item => item.providerId === providerId)
      : items;
    return filteredItems.reduce((total, item) => {
      const price = typeof item.price === 'string' 
        ? parseFloat(item.price.replace(/[$,]/g, '')) 
        : parseFloat(item.price) || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const getItemsByProvider = (providerId) => {
    return items.filter(item => item.providerId === providerId);
  };

  const getItemsByType = (itemType) => {
    return items.filter(item => item.itemType === itemType);
  };

  const getProviderIds = () => {
    return [...new Set(items.map(item => item.providerId))];
  };

  const getCartSummary = () => {
    const providers = getProviderIds();
    const summary = providers.map(providerId => {
      const providerItems = getItemsByProvider(providerId);
      const providerName = providerItems.length > 0 ? providerItems[0].providerName : 'Provider';
      
      return {
        providerId,
        providerName,
        items: providerItems,
        total: getCartTotal(providerId),
        products: providerItems.filter(item => item.itemType === 'product'),
        services: providerItems.filter(item => item.itemType === 'service'),
        offers: providerItems.filter(item => item.itemType === 'offer')
      };
    });
    
    return summary;
  };

  const clearProviderCart = async (providerId) => {
    try {
  //    console.log('🧹 [CART CONTEXT] Clearing cart for provider:', providerId);
      
      // Update local state immediately
      const updatedItems = items.filter(item => item.providerId !== providerId);
      setItems(updatedItems);
      const newTotalItems = updatedItems.reduce((total, item) => total + item.quantity, 0);
      setTotalItems(newTotalItems);
      
      // Update localStorage for guest users
      if (isGuest) {
        localStorage.setItem('cart', JSON.stringify({ items: updatedItems, totalItems: newTotalItems }));
  //      console.log('✅ [CART CONTEXT] Guest cart cleared locally for provider:', providerId);
      } else {
        // For authenticated users, also clear the backend cart
        try {
          const response = await cartAPI.clearProviderCart(providerId);
          if (response.success) {
   //         console.log('✅ [CART CONTEXT] Backend cart cleared for provider:', providerId);
          } else {
            console.warn('⚠️ [CART CONTEXT] Backend cart clear failed:', response.message);
          }
        } catch (error) {
          console.error('❌ [CART CONTEXT] Error clearing backend cart:', error);
          // Don't fail the operation if backend clear fails
        }
      }
    } catch (error) {
      console.error('❌ [CART CONTEXT] Error in clearProviderCart:', error);
    }
  };

  const forceRefreshCart = async () => {
    await refreshCart(0);
  };

  // Helper functions for Home page compatibility
  const isItemInCart = (item) => {
    return items.some(cartItem => {
      const idMatch = cartItem.id === item._id || cartItem.id === item.id;
      // For services, check if both items are services and have matching provider IDs
      if (item.providerId || item.provider?._id) {
        // This is a service - check if cart item is also a service with same provider
        return idMatch && cartItem.itemType === 'service' && cartItem.providerId === (item.providerId || item.provider?._id);
      } else {
        // This is a product - check if cart item is also a product
        return idMatch && cartItem.itemType === 'product';
      }
    });
  };

  const getItemQuantityInCart = (item) => {
    const cartItem = items.find(cartItem => {
      const idMatch = cartItem.id === item._id || cartItem.id === item.id;
      // For services, check if both items are services and have matching provider IDs
      if (item.providerId || item.provider?._id) {
        // This is a service - check if cart item is also a service with same provider
        return idMatch && cartItem.itemType === 'service' && cartItem.providerId === (item.providerId || item.provider?._id);
      } else {
        // This is a product - check if cart item is also a product
        return idMatch && cartItem.itemType === 'product';
      }
    });
    return cartItem ? cartItem.quantity : 0;
  };

  const updateItemQuantity = (item, newQuantity) => {
    const cartItem = items.find(cartItem => {
      const idMatch = cartItem.id === item._id || cartItem.id === item.id;
      // For services, check if both items are services and have matching provider IDs
      if (item.providerId || item.provider?._id) {
        // This is a service - check if cart item is also a service with same provider
        return idMatch && cartItem.itemType === 'service' && cartItem.providerId === (item.providerId || item.provider?._id);
      } else {
        // This is a product - check if cart item is also a product
        return idMatch && cartItem.itemType === 'product';
      }
    });

    if (cartItem) {
      updateQuantity(cartItem.cartItemId, newQuantity);
    }
  };

  const value = {
    items,
    totalItems,
    loading,
    error,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    clearProviderCart,
    getCartTotal,
    getItemQuantity,
    isInCart,
    getItemsByProvider,
    getItemsByType,
    getProviderIds,
    getCartSummary,
    refreshCart: forceRefreshCart,
    // Home page compatibility functions
    isItemInCart,
    getItemQuantityInCart,
    updateItemQuantity,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 